import * as THREE from 'three';

/** Bake-output modes. 0 is the live shaded path (identity). */
export const BAKE_OUTPUT_OFF = 0;
export const BAKE_OUTPUT_ALBEDO = 1;
export const BAKE_OUTPUT_ROUGHNESS = 2;
export const BAKE_OUTPUT_NORMAL = 3;

export interface SurfaceBakeUniforms {
  uBakeOutput: { value: number };
  uBakeTangent: { value: THREE.Vector3 };
  uBakeBitangent: { value: THREE.Vector3 };
  uBakeFaceNormal: { value: THREE.Vector3 };
}

const BAKE_UNIFORMS = new WeakMap<THREE.Material, SurfaceBakeUniforms>();

export function createSurfaceBakeUniforms(): SurfaceBakeUniforms {
  return {
    uBakeOutput: { value: BAKE_OUTPUT_OFF },
    uBakeTangent: { value: new THREE.Vector3(1, 0, 0) },
    uBakeBitangent: { value: new THREE.Vector3(0, 1, 0) },
    uBakeFaceNormal: { value: new THREE.Vector3(0, 0, 1) },
  };
}

export function getSurfaceBakeUniforms(material: THREE.Material): SurfaceBakeUniforms {
  let uniforms = BAKE_UNIFORMS.get(material);
  if (!uniforms) {
    uniforms = createSurfaceBakeUniforms();
    BAKE_UNIFORMS.set(material, uniforms);
  }
  return uniforms;
}

/**
 * Inject bake-output uniforms + a fragment override that writes unlit albedo /
 * roughness / tangent-space normals. Default uBakeOutput=0 is a visual no-op.
 */
export function applyBakeShaderHooks(
  shader: { uniforms: Record<string, { value: unknown }>; fragmentShader: string },
  material: THREE.Material,
): void {
  const uniforms = getSurfaceBakeUniforms(material);
  Object.assign(shader.uniforms, uniforms);
  if (!shader.fragmentShader.includes('uniform float uBakeOutput')) {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
uniform float uBakeOutput;
uniform vec3 uBakeTangent;
uniform vec3 uBakeBitangent;
uniform vec3 uBakeFaceNormal;
`,
    );
  }
  if (!shader.fragmentShader.includes('#include <opaque_fragment>')) return;
  if (shader.fragmentShader.includes('if (uBakeOutput > 0.5)')) return;
  shader.fragmentShader = shader.fragmentShader.replace(
    '#include <opaque_fragment>',
    `if (uBakeOutput > 0.5) {
  vec3 bakeRgb = diffuseColor.rgb;
  if (uBakeOutput < 1.5) {
    bakeRgb = diffuseColor.rgb;
  } else if (uBakeOutput < 2.5) {
    bakeRgb = vec3(roughnessFactor);
  } else {
    vec3 worldN = normalize((vec4(normal, 0.0) * viewMatrix).xyz);
    vec3 tsN = vec3(
      dot(worldN, uBakeTangent),
      dot(worldN, uBakeBitangent),
      dot(worldN, uBakeFaceNormal)
    );
    bakeRgb = tsN * 0.5 + 0.5;
  }
  gl_FragColor = vec4(bakeRgb, 1.0);
} else {
  #include <opaque_fragment>
}`,
  );
}

export function setBakeOutput(
  material: THREE.Material,
  mode: number,
  basis?: { tangent: THREE.Vector3; bitangent: THREE.Vector3; normal: THREE.Vector3 },
): void {
  const uniforms = getSurfaceBakeUniforms(material);
  uniforms.uBakeOutput.value = mode;
  if (basis) {
    uniforms.uBakeTangent.value.copy(basis.tangent);
    uniforms.uBakeBitangent.value.copy(basis.bitangent);
    uniforms.uBakeFaceNormal.value.copy(basis.normal);
  }
}
